import { router } from 'expo-router';
import { useCallback } from 'react';
import { useIntl } from 'react-intl';

import useEnrichCartEntries from '@fhs/cart/src/queries/useEnrichCartEntries';
import { useCartActions } from '@fhs/cart/src/state/cart-store';
import { useToast } from '@fhs-legacy/frontend/src/hooks/use-toast';
import { isTest } from '@fhs-legacy/frontend/src/utils/environment';
import { TOAST_ITEM_ADDED_TO_CART } from '@fhs-legacy/frontend/src/utils/test-ids';
import { IBackendCartEntries } from '@rbi-ctg/menu';
import { routes } from 'utils/routing';

/**
 *
 * Returns a function to reorder a previous order.
 */
export const useHandleReorder = ({
  setPendingReorder,
  setReordering,
  storeHasSelection,
  setUnavailableCartEntries,
  setReorderedOrderId,
}) => {
  const { addReorderItemsToCart } = useCartActions();

  const { formatMessage } = useIntl();
  const toast = useToast();

  const { fetchEnrichedCartEntries, loadingEnrichCartEntries } = useEnrichCartEntries();

  const handleReorder = useCallback(
    async (order, url = routes.orders) => {
      if (order && storeHasSelection) {
        setReordering(true);
        setReorderedOrderId(order.rbiOrderId);
        const { reorderCartEntries } = order.cart;
        const enrichedCartEntries = await fetchEnrichedCartEntries(reorderCartEntries);
        if (!enrichedCartEntries?.data || enrichedCartEntries.errors) {
          return;
        }

        const enrichedUnavailableItems = enrichedCartEntries?.data?.unavailableItems || [];
        if (enrichedUnavailableItems.length) {
          setUnavailableCartEntries(enrichedUnavailableItems);
        }

        setReordering(false);
        setReorderedOrderId(null);
        setPendingReorder(null);

        addReorderItemsToCart(enrichedCartEntries.data.entries as unknown as IBackendCartEntries[]);
        if (enrichedUnavailableItems.length === 0) {
          toast.show({
            testID: TOAST_ITEM_ADDED_TO_CART,
            duration: isTest ? 9999999 : undefined,
            text: formatMessage({ id: 'addToCartSuccessReorder' }),
            variant: 'positive',
          });

          router.push(routes.cart);
        } else {
          // Navigate to the url provided if there are unavailable items
          router.navigate(url);
        }
      } else {
        // if there's no order or store selection, navigate to where was initially intended
        router.navigate(url);
      }
    },
    [
      storeHasSelection,
      setReordering,
      setReorderedOrderId,
      fetchEnrichedCartEntries,
      setPendingReorder,
      setUnavailableCartEntries,
      addReorderItemsToCart,
      toast,
      formatMessage,
    ]
  );

  return { handleReorder, loadingEnrichCartEntries };
};
